import api from './api'
import type { Contact, ContactCustomField, PaginatedResponse } from '@/types'
import type {
  ApiResponse,
  CreateContactCustomFieldRequest,
  CreateContactRequest,
  UpdateContactCustomFieldRequest,
  UpdateContactRequest,
} from '@/types/api'

const ContactsService = {
  async getAll(params?: {
    search?: string
    instance_id?: string
    avatar_status?: 'pending' | 'fetched' | 'not_found' | 'error'
    page?: number
    per_page?: number
  }): Promise<PaginatedResponse<Contact>> {
    const response = await api.get<PaginatedResponse<Contact>>('/api/v1/contacts', {
      params,
    })
    return response.data
  },

  async getById(contactId: string): Promise<Contact> {
    const response = await api.get<ApiResponse<Contact>>(`/api/v1/contacts/${contactId}`)
    return response.data.data
  },

  async create(data: CreateContactRequest): Promise<Contact> {
    const response = await api.post<ApiResponse<Contact>>('/api/v1/contacts', data)
    return response.data.data
  },

  async update(contactId: string, data: UpdateContactRequest): Promise<Contact> {
    const response = await api.put<ApiResponse<Contact>>(`/api/v1/contacts/${contactId}`, data)
    return response.data.data
  },

  async getCustomFields(contactId: string): Promise<ContactCustomField[]> {
    const response = await api.get<ApiResponse<ContactCustomField[]>>(
      `/api/v1/contacts/${contactId}/custom-fields`,
    )
    return response.data.data
  },

  async createCustomField(
    contactId: string,
    data: CreateContactCustomFieldRequest,
  ): Promise<ContactCustomField> {
    const response = await api.post<ApiResponse<ContactCustomField>>(
      `/api/v1/contacts/${contactId}/custom-fields`,
      data,
    )
    return response.data.data
  },

  async updateCustomField(
    contactId: string,
    fieldId: string,
    data: UpdateContactCustomFieldRequest,
  ): Promise<ContactCustomField> {
    const response = await api.put<ApiResponse<ContactCustomField>>(
      `/api/v1/contacts/${contactId}/custom-fields/${fieldId}`,
      data,
    )
    return response.data.data
  },

  async deleteCustomField(contactId: string, fieldId: string): Promise<void> {
    await api.delete(`/api/v1/contacts/${contactId}/custom-fields/${fieldId}`)
  },

  async delete(contactId: string): Promise<void> {
    await api.delete(`/api/v1/contacts/${contactId}`)
  },
}

export default ContactsService
